import { DesignLayoutType, BrannanNavigation } from '@graphql/generated-base';
import type { AccommodationsSectionLayoutType } from '@shared/types/Accommodations.types';
import type { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';

// Mirrors the relevant fields of EventDesignFragment as a narrow input shape — keeps this
// util usable from any consumer (admin selector, future guest-side fallbacks) without
// coupling to one query's full fragment. All fields accept null/undefined for loading state;
// callers pass null-coalesced values from the optional `eventDesign` context.
//
// `presentationLayout` comes from `eventDesign.activeWebsiteHeaderPresentationLayout.dataJSON.presentationLayout`
// — when the value is `'fullWidth'` the header photo spans the viewport with no decorative
// art frame on the sides. Templates like photoArch/photoDuo/photoTrio ALSO use Brannan +
// singlePage navigation but have art elements, so brannanNavigation alone is insufficient.
export type WebsiteDesignContext = Readonly<{
  layoutType?: DesignLayoutType | null;
  brannanNavigation?: BrannanNavigation | null;
  presentationLayout?: PhotoPresentationLayout | null;
}>;

export const isAccommodationsLayoutAvailable = (layout: AccommodationsSectionLayoutType, design: WebsiteDesignContext): boolean => {
  const isAloha = design.layoutType === DesignLayoutType.aloha;
  // "Full-width Brannan without art" = Brannan layout + single-page navigation + fullWidth
  // header photo presentation. All three required because singlePage Brannan templates exist
  // with art (photoArch, photoDuo, etc.) and we only want full_width_photo on the truly
  // borderless variant.
  const isBrannanFullWidthNoArt =
    design.layoutType === DesignLayoutType.brannan && design.brannanNavigation === BrannanNavigation.singlePage && design.presentationLayout === 'fullWidth';

  switch (layout) {
    case 'side_by_side_elegant':
      // Hidden on Aloha; visible on all Brannan variants (art or no art).
      return !isAloha;
    case 'full_width_photo':
      // Visible only on full-width Brannan without art on the sides.
      return isBrannanFullWidthNoArt;
    default:
      return true;
  }
};
